import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { FaSearch, FaFilter, FaChevronDown } from 'react-icons/fa';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { showError } from '../utils/toast';

const PolicyDuePage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [policiesData, setPoliciesData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAlert, setShowAlert] = useState(true); // ✅ moved hook to top level

  const formatDate = (date) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.id) {
          console.warn("❌ User ID not found.");
          return;
        }
        console.log("📥 Fetching policies for user ID:", user.id)
        const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/customer/customer-fetch-policy/${user.id}`);
        const data = await res.json();
        console.log("📊 Fetched data:", data);

        if (res.ok) {
          setPoliciesData(data.policyData);
        } else {
          console.warn("⚠️ Unexpected policy data structure:", data);
          showError(data.msg || "Failed to fetch policies");
        }
      } catch (err) {
        console.error("🚫 Failed to fetch policies:", err);
      }
    };

    fetchPolicies();
  }, []);

  const policyTypes = ['All', ...new Set(policiesData.map(p => p?.policyType))];
  const filteredPolicies = policiesData.filter(policy => {
    const matchesSearch =
      policy.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      policy.policyNumber.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === 'All' || policy.policyType === filterType;
    
    const matchesDate = !selectedDate || selectedDate.toLocaleDateString('en-GB') === policy.endDate;
    return matchesSearch && matchesType && matchesDate;
  });

  // count alerts
  const dueCount = policiesData.filter(policy => policy.status === "Due").length;
  const overdueCount = policiesData.filter(policy => policy.status === "Overdue").length;

  return (
    <div className="policy-due-modern dashboard-layout d-flex">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      <div className="main-content">
        {/* Header Section */}
        <div className="page-header">
          <h1 className="page-title">Policy Due</h1>
          <p className="page-subtitle">Monitor and manage your insurance policy payment schedule</p>
        </div>

        {/* Alert Box */}
        {showAlert && (dueCount > 0 || overdueCount > 0) && (
          <div className="alert-notification">
            <div className="alert-icon">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>

            <div className="alert-content">
              <div className="alert-text">
                You have <span className="overdue-count">{overdueCount} overdue</span> and{" "}
                <span className="due-count">{dueCount} due</span> policies
              </div>
              <div className="alert-subtext">
                Immediate attention required for overdue policies
              </div>
            </div>

            {/* Close Button */}
            <button className="alert-close-btn" onClick={() => setShowAlert(false)}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}

        {/* Filters Section */}
        <div className="filters-container">
          <div className="search-input">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Search policies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-dropdown">
            <i className="bi bi-calendar3"></i>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              customInput={
                <input 
                  className="filter-input"
                  value={formatDate(selectedDate)}
                  placeholder="All Dates"
                  readOnly
                />
              }
              dateFormat="dd/MM/yyyy"
              placeholderText="All Dates"
            />
            <i className="bi bi-chevron-down"></i>
          </div>

          <div className="filter-dropdown">
            <i className="bi bi-funnel"></i>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-input"
            >
              {(policyTypes || [])
                .filter(type => type && type.trim() !== "")
                .map((type, i) => (
                  <option key={i} value={type}>{type}</option>
                ))}
            </select>
            <i className="bi bi-chevron-down"></i>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-container">
          <table className="modern-table ">
            <thead>
              <tr>
                <th>Policy</th>
                <th>Policy no</th>
                <th>Insurance</th>
                <th>Due Date</th>
                <th>Premium</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredPolicies.map((policy, idx) => (
                <tr key={idx}>
                  <td className="policy-name">
                    {policy.productName
                      ? policy.productName.split(" ").slice(0, 3).join(" ")
                      : "N/A"}
                  </td>

                  <td className="policy-number">{policy.policyNumber}</td>
                  <td className="insurance-type">{policy.policyType}</td>
                  <td className="due-date">{policy.endDate}</td>
                  <td className="premium-amount">₹{policy.premiumAmount}</td>
                  <td>
                    <span className={`status-badge ${policy.status.toLowerCase()}`}>
                      <i className={`bi ${
                        policy.status === 'Due' ? 'bi-clock' : 
                        policy.status === 'Overdue' ? 'bi-exclamation-triangle' : 'bi-check-circle'
                      }`}></i>
                      {policy.status}
                    </span>
                  </td>
                  <td>
                    {(policy.status === 'Due' || policy.status === 'Overdue') && (
                      <button className={`action-btn ${policy.status === 'Overdue' ? 'overdue-btn' : 'pay-btn'}`}>
                        <i className={`bi ${policy.status === 'Overdue' ? 'bi-exclamation-triangle' : 'bi-credit-card'}`}></i>
                        {policy.status === 'Overdue' ? 'Pay Overdue' : 'Pay Now'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <button className="pagination-btn" disabled>
            <i className="bi bi-chevron-left"></i>
            Previous
          </button>
          
          <div className="page-numbers">
            <button className="page-number active">1</button>
            <button className="page-number">2</button>
          </div>

          <button className="pagination-btn">
            Next
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyDuePage;
